import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import type { UpdatePresetPayload, PresetResponse } from '../types';
import { presetApi } from '../api';
import { presetKeys } from './preset.keys';

type UpdatePresetInput = {
  id: string;
  data: Omit<UpdatePresetPayload, 'cover' | 'lut'>;
};

export function useUpdatePreset(
  options?: UseMutationOptions<PresetResponse, Error, UpdatePresetInput>
) {
  const queryClient = useQueryClient();
  const { onSuccess: providedOnSuccess, onError: providedOnError, ...mutationOptions } = options ?? {};
  const mutation = useMutation({
    mutationKey: ['presets', 'update'],
    mutationFn: ({ id, data }: UpdatePresetInput) => presetApi.update(id, data),
    ...mutationOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      toast({ title: 'Preset atualizado com sucesso', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: presetKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: presetKeys.all });
      providedOnSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao atualizar preset',
        description: error.message,
        variant: 'destructive',
      });
      providedOnError?.(error, variables, onMutateResult, context);
    },
  });
  const updatePreset = (id: string, data: UpdatePresetInput['data']) =>
    mutation.mutateAsync({ id, data });
  return {
    updatePreset,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
